<template>
  <div class="networks-card desktop-shadow-card desktop-white-bg rounded">
    <div class="card-header">
      <h4 class="card-header-title">
        <icon type="project-diagram" class="mr-2" /> Network Requests
        <i
          class="fa fa-info-circle cursor-pointer ml-2 tooltip-info"
          v-tooltip="
            'Once you approve the requests, you will be part of their network'
          "
        ></i>
      </h4>
      <div class="d-flex justify-content-end my-3 my-md-0">
        <div class="d-flex align-items-center mr-auto mr-md-5 text-dark">
          <span>
            <b v-if="!loading">
              <i class="fas fa-spin fa-circle-notch" v-if="busy"></i>
              {{ busy ? "" : networkRequests.length }}/{{ total }}</b
            >
            <i class="fas fa-spin fa-circle-notch" v-if="loading"></i>
            Connection{{ loading || total != 1 ? "s" : "" }}
          </span>
        </div>
        <select
          v-model="selectedStatus"
          :disabled="loading"
          @change="getNetworkRequests()"
          class="form-control row m-0 w-auto"
        >
          <option
            v-for="status in statuses"
            :value="status.value"
            :key="status.value"
          >
            {{ status.label }}
          </option>
        </select>
      </div>
    </div>
    <alert
      v-if="!networkRequests.length && !loading"
      class="my-4 mx-lg-4 mobile-tablet-white-bg mobile-tablet-shadow-card"
      >No Connections to display</alert
    >
    <alert
      v-if="loading"
      type="loader"
      color="light"
      class="my-4 mx-lg-4 mobile-tablet-white-bg mobile-tablet-shadow-card"
    />
    <div v-if="!loading && networkRequests.length" class="px-lg-4 pt-lg-4">
      <div
        class="col-12 card p-3 mobile-tablet-shadow-card"
        v-for="network of networkRequests"
        :key="network.id"
      >
        <div
          class="row vendor-info-row mt-3 justify-content-center relative-position"
        >
          <div class="col-4 col-md vendor-logo-col">
            <img
              :src="network.logoUrl || '/images/no-logo.png'"
              class="rounded-circle w-100"
            />
          </div>
          <div class="col-12 col-md-10 mt-4">
            <h1 class="header-title bold text-capitalize">
              {{ network.name }}
            </h1>
            <p class="text-dark mt-2 bio-text">
              <icon class="mr-3" type="align-left" />
              {{ network.bio || "Bio not available." }}
            </p>
          </div>
          <div class="vendor-avatar">
            <img
              :src="`/images/map-avatars/${network.avatar_type || 0}.png`"
              class="avatar-img shadow-card m-0 cursor-pointer border-0"
              v-tooltip.top="vendorAvatars[network.avatar_type || 0].tooltip"
            />
          </div>
        </div>
        <div v-if="network.user" class="mt-3">
          <b>Requested By: </b>{{ network.user }}
        </div>
        <div
          class="border-top mt-5 text-center pt-3 pb-2"
          v-if="!network.handling && userHasControl"
        >
          <button
            @click="network.status != 2 && changeNetworkStatus(network, 2)"
            class="btn btn-success btn-sm"
            :disabled="network.status == 2"
          >
            Approve <i class="fas fa-thumbs-up"></i>
          </button>
          <button
            @click="network.status != 3 && changeNetworkStatus(network, 3)"
            :disabled="network.status == 3"
            class="btn btn-danger btn-sm ml-4"
          >
            Reject <i class="fas fa-thumbs-down"></i>
          </button>
        </div>
        <div
          class="border-top mt-3 text-center pt-3 pb-2"
          v-if="network.status || network.handling"
        >
          <h4
            v-if="!network.handling"
            :class="
              network.status == 1
                ? 'text-warning'
                : network.status == 2
                ? 'text-success'
                : 'text-danger'
            "
          >
            {{
              network.status == 1
                ? "Pending"
                : network.status == 2
                ? "Approved"
                : "Rejected"
            }}
          </h4>
          <span
            class="spinner-border spinner-border-sm"
            v-if="network.handling"
            role="status"
            aria-hidden="true"
          ></span>
        </div>
      </div>
    </div>
    <mugen-scroll :handler="loadMore" :should-handle="!busy && !loading">
      <alert
        v-if="busy"
        class="text-center p-2 mb-0 mobile-tablet-loadmore mobile-tablet-shadow-card"
        ><i class="fas fa-spin fa-circle-notch mr-2"></i>Loading more
        connections...</alert
      >
    </mugen-scroll>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Swal from "sweetalert2";
import MugenScroll from "vue-mugen-scroll";
import Auth from "../../../auth";
import constants from "../constants";

export default {
  name: "vendorNetworksRequests",
  components: {
    MugenScroll,
  },
  data() {
    return {
      statuses: [
        { value: -1, label: "Status" },
        { value: 1, label: "Pending" },
        { value: 2, label: "Approved" },
        { value: 3, label: "Rejected" },
      ],
      selectedStatus: -1,
      limit: 50,
      busy: false,
      vendorAvatars: constants.AVATARS,
    };
  },
  computed: {
    ...mapState(["vendors"]),
    ...mapState({
      networkRequests: (state) => state.networks.requestsPagination.data,
      current: (state) => state.networks.requestsPagination.current,
      total: (state) => state.networks.requestsPagination.total || 0,
      loading: (state) => state.networks.loading,
    }),
    vendor: function () {
      return this.vendors.single;
    },
    loggedInUser: function () {
      return Auth.user.get();
    },
    userHasControl: function () {
      if (!this.vendor) {
        return false;
      }
      if (
        this.loggedInUser.isAdmin ||
        ((this.vendor.admins || []).length &&
          this.vendor.admins.includes(this.loggedInUser.id))
      ) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    if (!this.vendor) {
      this.$store.dispatch("vendors/get", { id: this.$route.params.id });
    }
    this.getNetworkRequests();
  },
  methods: {
    ...mapActions({
      getVendorNetworkRequests: "networks/getRequests",
    }),
    changeNetworkStatus(network, status) {
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: `You are going to ${
            status == 2 ? "approve" : "reject"
          } this request`,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: `Yes, ${status == 2 ? "approve" : "reject"}`,
          cancelButtonText: "No, cancel!",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            network.handling = true;
            this.$forceUpdate();
            this.$store
              .dispatch("networks/changeNetworkStatus", {
                id: network.id,
                status,
              })
              .then((success) => {
                network.handling = false;
                if (success) {
                  network.status = status;
                }
                this.$forceUpdate();
              });
          }
        });
    },
    getNetworkRequests(page) {
      const payload = {
        limit: this.limit,
        id: this.$route.params.id,
      };
      if (this.selectedStatus != -1) {
        payload.status = this.selectedStatus;
      }
      if (page) {
        payload.page = page;
      }
      this.getVendorNetworkRequests(payload).then(() => {
        this.busy = false;
      });
    },
    loadMore() {
      if (this.networkRequests.length < this.total) {
        this.busy = true;
        this.getNetworkRequests(this.current + 1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.vendor-logo-col {
  max-width: 130px !important;
}

.vendor-avatar {
  position: absolute;
  right: 16px;
  top: -8px;
  .avatar-img {
    width: 56px;
    max-height: 56px;
    border-radius: 50%;
  }
}

@media (max-width: 1024px) {
  .networks-card {
    .card-header {
      padding: 0 !important;
    }
  }
}

@media (max-width: 767px) {
  .networks-card {
    .card-header {
      display: block;
      height: auto;
    }
  }
}
</style>
